import { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import CustomizeOption from '../components/CustomizeOption'
import CustomizeModal from '../components/CustomizeModal';
import DateTypeSwitch from './When/DateTypeSwitch';
import MonthType from './When/MonthType';
import RangeType from './When/RangeType';
import SpecificType from './When/SpecificType';
import format from '../../../utilities/format';
import { parseDate } from '../../../utilities/helpers';
import { t } from '../../../utilities/translator';
import { pushEvent } from '../../../utilities/analytics';
import { CUSTOMIZE_WHEN_SAVED, CUSTOMIZE_WHEN_CLICKED } from '../../../constants/analyticEvents';
import { CUSTOMIZE, DATE_TYPES } from '../../../constants/constants';
import { setPreselectedDateType, toggleCustomizePanel } from '../customizeButtonSlice';
import { intervalToDuration } from 'date-fns';
import { SwitchTransition } from 'react-transition-group';
import FadeTransition from '../../../components/animations/FadeTransition';

const When = ({ onSave, selected }) => {
  const dispatch = useDispatch();
  const { activePanel, preselectedDateType } = useSelector(state => state.customize);
  const [selectedActiveType, setSelectedActiveType] = useState(preselectedDateType || selected.type);
  const [selectedStartDate, setSelectedStartDate] = useState(selected.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(selected.endDate);
  const [selectedMonths, setSelectedMonths] = useState(selected.months);
  const [selectedNights, setSelectedNights] = useState(selected.nights);
  const [selectedWeekendsOnly, setSelectedWeekendsOnly] = useState(selected.weekendsOnly);

  useEffect(() => {
    setSelectedActiveType(preselectedDateType || selected.type);
    setSelectedStartDate(selected.startDate);
    setSelectedEndDate(selected.endDate);
    setSelectedMonths(selected.months);
    setSelectedNights(selected.nights);
    setSelectedWeekendsOnly(selected.weekendsOnly);
  }, [selected, preselectedDateType])

  const getOptionLabel = () => {
    let label;
    let nightsLabel = '';

    const getStartDate = selectedStartDate
    ? format(selectedStartDate, 'dd MMM')
    : t('mainFilter.any');
    const getEndDate = selectedEndDate
      ? format(selectedEndDate, 'dd MMM')
      : t('mainFilter.any');

    if (selectedNights?.length > 0) {
      nightsLabel = `${[...selectedNights].sort((a, b) => a - b).join(', ')} ${t(
        'dealItem.nights_plural',
        { count: 2 }
      )}`;
    }

    if (selected.type === DATE_TYPES.MONTH) {
      label = `${[...selectedMonths]
        .sort((a, b) => a - b)
        .map((m) => format(parseDate(m, 'M'), 'MMM'))
        .join(', ')}`;
    }

    if (selected.type === DATE_TYPES.RANGE || selected.type === DATE_TYPES.SPECIFIC) {
      label = `${getStartDate} - ${getEndDate}`
    }

    if (selected.type === DATE_TYPES.MONTH || selected.type === DATE_TYPES.RANGE) {
      if (label.length > 0 && nightsLabel.length > 0) {
        label += ` - ${nightsLabel}`;
      } else {
        label += nightsLabel;
      }
    }

    return label || t('mainFilter.any_dates');
  }

  const handleOptionClick = () => {
    dispatch(toggleCustomizePanel(CUSTOMIZE.WHEN));
    pushEvent(CUSTOMIZE_WHEN_CLICKED);
  }

  const handleClose = () => {
    onClose();
    setSelectedMonths(selected.months);
    setSelectedNights(selected.nights);
    setSelectedActiveType(selected.type);
    setSelectedWeekendsOnly(selected.weekendsOnly);
    setSelectedStartDate(selected.startDate);
    setSelectedEndDate(selected.endDate);
  }

  const onClose = () => {
    dispatch(toggleCustomizePanel(null));
    dispatch(setPreselectedDateType(null));
  }

  const onDateChange = (dates) => {
    const [start, end] = dates;
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  const handleMonthClick = (month) => {
    let monthNumber = +month;
    if (selectedMonths.includes(monthNumber)) {
      setSelectedMonths(selectedMonths.filter(month => month !== monthNumber));
    } else {
      setSelectedMonths([...selectedMonths, monthNumber]);
    }
  }

  const handleNightClick = (day) => {
    if (selectedNights.includes(day)) {
      setSelectedNights(selectedNights.filter(selectedDay => selectedDay !== day));
    } else {
      setSelectedNights([...selectedNights, day]);
    }
    if (day > 3) {
      setSelectedWeekendsOnly(false);
    }
  }

  const handleWeekendsOnlyClick = useCallback(() => {
    setSelectedNights(selectedWeekendsOnly ? [] : [2, 3]);
    setSelectedWeekendsOnly((weekendsOnly) => !weekendsOnly);
  }, [selectedWeekendsOnly])

  const handleSave = () => {
    onClose();

    if (selectedActiveType === DATE_TYPES.MONTH) {
      onSave({
        type: selectedActiveType,
        months: selectedMonths,
        minNights: selectedNights.length > 0 ? Math.min(...selectedNights) : undefined,
        maxNights: selectedNights.length > 0 ? Math.max(...selectedNights) : undefined,
        nights: selectedNights || [],
        weekendsOnly: selectedWeekendsOnly,
      })
    }

    if (selectedActiveType === DATE_TYPES.RANGE) {
      onSave({
        type: selectedActiveType,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        weekendsOnly: selectedWeekendsOnly,
        minNights: selectedNights.length > 0 ? Math.min(...selectedNights) : undefined,
        maxNights: selectedNights.length > 0 ? Math.max(...selectedNights) : undefined,
        nights: selectedNights || [],
        months: [],
      })
    }

    if (selectedActiveType === DATE_TYPES.SPECIFIC) {
      onSave({
        type: selectedActiveType,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        nights: selectedNights || [],
        months: [],
      })
    }
    pushEvent(CUSTOMIZE_WHEN_SAVED, { whenType: selectedActiveType });
  }

  const handleClear = () => {
    onSave({
      startDate: null,
      endDate: null,
      months: [],
      type: DATE_TYPES.MONTH,
      nights: [],
      weekendsOnly: false,
    })

    onClose();
  }

  let daysInterval = 20;

  if (selectedStartDate && selectedEndDate) {
    daysInterval = intervalToDuration({
      start: selectedStartDate,
      end: selectedEndDate,
    }).days;
  }

  useEffect(() => {
    if (selectedActiveType === 'range' && selectedStartDate && selectedEndDate) {
      setSelectedNights(
        selectedNights.filter((night) => night <= daysInterval)
      );
    }
  }, [selectedActiveType, selectedStartDate, selectedEndDate]);

  return (
    <>
      <CustomizeOption
        label={t('mainFilter.when')}
        value={getOptionLabel()}
        icon="ri-calendar-line"
        onClick={handleOptionClick}
      />

      <CustomizeModal show={activePanel === CUSTOMIZE.WHEN} close={handleClose}>
        <CustomizeModal.Header close={handleClose} />


        <CustomizeModal.Body>
          <div className="text-xl font-semibold text-start">{t('mainFilter.when')}</div>
          <DateTypeSwitch activeType={selectedActiveType} onClick={setSelectedActiveType} />
          <SwitchTransition mode="out-in">
            <FadeTransition key={selectedActiveType}>
              <div className="flex flex-col gap-6">
                {selectedActiveType === DATE_TYPES.MONTH && (
                  <MonthType
                    selectedMonths={selectedMonths}
                    selectedDays={selectedNights}
                    weekendsOnly={selectedWeekendsOnly}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleNightClick}
                    onWeekendsOnlyClick={handleWeekendsOnlyClick}
                  />
                )}
                {selectedActiveType === DATE_TYPES.RANGE && (
                  <RangeType
                    endDate={selectedEndDate}
                    startDate={selectedStartDate}
                    selectedDays={selectedNights}
                    weekendsOnly={selectedWeekendsOnly}
                    onDayClick={handleNightClick}
                    onWeekendsOnlyClick={handleWeekendsOnlyClick}
                    onDateChange={onDateChange}
                    daysInterval={daysInterval}
                  />
                )}
                {selectedActiveType === DATE_TYPES.SPECIFIC && (
                  <SpecificType
                    endDate={selectedEndDate}
                    startDate={selectedStartDate}
                    onDateChange={onDateChange}
                  />
                )}
              </div>
            </FadeTransition>
          </SwitchTransition>
        </CustomizeModal.Body>

        <CustomizeModal.Footer
          close={handleClose}
          onSave={handleSave}
          onClear={handleClear}
        />
      </CustomizeModal>
    </>
  )
}

export default memo(When)
